import { useState, useEffect, useMemo, useRef } from "react";
import useIsMobile from "../client/hooks/useIsMobile";
import { startOfMonth } from "date-fns";
import {
  getTradingSessionsByUser,
  getAllTradesForAnalytics,
  getPendingSessionMerges,
  getAccountsByUser,
  getAccountGroupsByUser,
  getTradingStrategiesByUser,
  confirmSessionMerge,
  dismissSessionMerge,
  getActiveLayout,
  ensureSessionsDefaultLayout,
  resetLayoutContent,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { PaymentPlanId } from "../payment/plans";
import { Layers, ChevronDown, Loader2, Plus, GitMerge, X } from "lucide-react";
import { Button } from "../client/components/ui/button";
import { toast } from "../client/hooks/use-toast";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { useOnboardingStatus } from "../client/hooks/useOnboardingStatus";
import { AddSessionDialog } from "./AddSessionDialog";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";
import { type AccountFilterValue } from "../trading-accounts/AccountGroupFilter";
import { motion } from "framer-motion";
import { PageTransition } from "../client/components/ui/motion";
import { type DateRange } from "../client/components/DateRangePicker";
import { presetToDateRange } from "../client/utils/dateRange";
import { getDefaultDateRange } from "../custom-pages/topbarDefaults";
import { useIsDark, useThemeId } from "../client/components/ui/card-section-header";
import { WidgetDataProvider, type DashboardData } from "../dashboard/widgets/WidgetDataContext";
import DashboardLayout from "../dashboard/widgets/DashboardLayout";
import EditModeBar from "../dashboard/widgets/EditModeBar";
import AddWidgetPicker, { TOPBAR_WIDGET_KEYS } from "../dashboard/widgets/AddWidgetPicker";
import ResetLayoutDialog from "../dashboard/widgets/ResetLayoutDialog";
import LayoutLibraryModal from "../dashboard/widgets/LayoutLibraryModal";
import { SortableTopbarSlot, TopbarDndWrapper } from "../dashboard/widgets/TopbarSlotRenderer";
import TopbarFilterBar from "../dashboard/widgets/TopbarFilterBar";
import { useActiveLayout, compactSlots } from "../dashboard/widgets/useActiveLayout";
import { useLayoutsLibrary } from "../dashboard/widgets/useLayoutsLibrary";
import { getDefaultSessionsLayout, withDefaultLayoutFallback } from "../dashboard/widgets/registry";
import type { LayoutSnapshot, SlotPlacement, WidgetKey } from "../dashboard/widgets/types";
import { trackWidgetAdded, trackWidgetRemoved, trackLayoutSaved } from "../client/utils/posthog";
import EdgyHeaderButton from "../edgy/EdgyHeaderButton";
import {
  calculateMetrics,
  calculateSymbolStats,
  calculateMonthlyStats,
  calculateCumulativePnl,
  calculatePnlDistribution,
  calculateRollingWinRate,
  calculateHourDayHeatmap,
  calculateTimeSlotStats,
  calculateHoldDurationData,
  calculateTagStats,
  calculateBehavioralBiases,
  calculateSessionStats,
  calculatePipStats,
  calculatePipBuckets,
  calculatePipsBySymbol,
  calculateCalmarRatio,
  calculateRecoveryFactor,
  calculateSizingData,
  calculateLongShortStats,
  calculateDayOfWeekStats,
  calculateStreakData,
  calculateConsistencyScore,
  calculateNetDailyPnl,
  calculateDrawdownCurve,
} from "../trading-journal/analytics";

export default function TradingSessionsPage() {
  const t = useTranslation();
  const isDark = useIsDark();
  const themeId = useThemeId();
  const { toast: toastFn } = useToast();
  const { data: user } = useAuth();

  const isFreePlan = !user?.subscriptionPlan || user.subscriptionPlan === PaymentPlanId.Free;

  // — Onboarding banner —
  const { steps } = useOnboardingStatus(user?.id);
  const sessionStep = steps.find((s) => s.id === "session-detail");
  const showOnboardingBanner =
    sessionStep !== undefined &&
    !sessionStep.completed &&
    !(user as any)?.onboardingCompletedAt;

  // — Data queries —
  const { data: sessions = [], refetch: refetchSessions } = useQuery(getTradingSessionsByUser);
  const { data: statsTradesList = [] } = useQuery(getAllTradesForAnalytics, { excludeInactive: false });
  const { data: pendingMerges = [], refetch: refetchMerges } = useQuery(getPendingSessionMerges);
  const { data: accountsData } = useQuery(getAccountsByUser);
  const accountsList = Array.isArray((accountsData as any)?.accounts) ? (accountsData as any).accounts : [];
  const hiddenAccountsCount: number = (accountsData as any)?.hiddenAccountsCount ?? 0;
  const { data: accountGroups = [] } = useQuery(getAccountGroupsByUser);
  const { data: strategies = [] } = useQuery(getTradingStrategiesByUser);

  // — Filter state (topbar) —
  // Sessions default to the widest range ("all_time"): a trader wants to see
  // every recorded session, not just the last 30 days.
  const [dateRange, setDateRange] = useState<DateRange>(() => presetToDateRange("all_time"));
  const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: "all" });
  const [strategyIds, setStrategyIds] = useState<string[]>([]);

  // Apply the topbar date filter on the page so every widget (sessions list,
  // last session, analytics, PnL) reacts. Sessions are kept when their start
  // date is in range; trades when their entry date is in range. Null bounds
  // (all_time) keep everything.
  const inDateRange = (d: Date | string | null | undefined): boolean => {
    if (!d) return false;
    const t = new Date(d).getTime();
    if (dateRange.from && t < dateRange.from.getTime()) return false;
    if (dateRange.to && t > dateRange.to.getTime()) return false;
    return true;
  };
  const filteredSessions = useMemo(
    () => (sessions as any[]).filter((s) => inDateRange(s.startedAt)),
    [sessions, dateRange],
  );
  const filteredTrades = useMemo(
    () => (statsTradesList as any[]).filter((tr) => inDateRange(tr.date)),
    [statsTradesList, dateRange],
  );
  // Apply the pinned topbar-date-filter default once the layout loads (lets the
  // user pin a favourite range like every other page). Falls back to all_time.
  const filterInitialized = useRef(false);

  // — Modal state —
  const [showPlanModal, setShowPlanModal] = useState(false);

  // — Add-Session dialog state (ported from SessionsView) —
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // — Session actions —
  const confirmMergeFn = useAction(confirmSessionMerge);
  const dismissMergeFn = useAction(dismissSessionMerge);

  const handleConfirmMerge = async (id: string) => {
    try {
      await confirmMergeFn({ id });
      refetchMerges();
      refetchSessions();
    } catch (e: any) {
      toast({ title: t.common.error, description: e.message, variant: "destructive" });
    }
  };

  const handleDismissMerge = async (id: string) => {
    try {
      await dismissMergeFn({ id });
      refetchMerges();
      refetchSessions();
    } catch (e: any) {
      toast({ title: t.common.error, description: e.message, variant: "destructive" });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Layout system (scope "sessions") — mirrors DashboardView / TradingJournalPage
  // ─────────────────────────────────────────────────────────────────────────
  const { data: serverLayout, refetch: refetchLayout } = useQuery(getActiveLayout, { scope: "sessions" });

  const ensureSessionsFn = useAction(ensureSessionsDefaultLayout);
  const ensureCalledRef = useRef(false);
  useEffect(() => {
    if (ensureCalledRef.current) return;
    ensureCalledRef.current = true;
    ensureSessionsFn(undefined).then(() => refetchLayout()).catch(() => {});
  }, []);

  const initialLayout: LayoutSnapshot = useMemo(() => {
    if (!serverLayout) return getDefaultSessionsLayout();
    const rows = [...serverLayout.rows].sort((a, b) => a.order - b.order);
    const allSlots: SlotPlacement[] = [];

    const isNewStyle = rows.length === 0 || rows.every((r) => r.cols === 6);

    if (isNewStyle) {
      for (const row of rows) {
        for (const s of row.slots) {
          allSlots.push({
            id: s.id,
            widgetKey: s.widgetKey as WidgetKey,
            colStart: s.colStart,
            rowStart: row.order,
            colSpan: s.colSpan,
            rowSpan: s.rowSpan,
            ...(s.notebookNoteId ? { notebookNoteId: s.notebookNoteId } : {}),
            ...((s as any).placement ? { placement: (s as any).placement } : {}),
            ...((s as any).defaultFilterValue != null ? { defaultFilterValue: (s as any).defaultFilterValue } : {}),
            ...((s as any).viewMode != null ? { viewMode: (s as any).viewMode } : {}),
          });
        }
      }
    } else {
      let cursor = 1;
      for (const row of rows) {
        const maxRowSpan = row.slots.reduce((m: number, s: any) => Math.max(m, s.rowSpan ?? 1), 1);
        for (const s of row.slots) {
          allSlots.push({
            id: s.id,
            widgetKey: s.widgetKey as WidgetKey,
            colStart: s.colStart,
            rowStart: cursor,
            colSpan: s.colSpan,
            rowSpan: s.rowSpan,
            ...(s.notebookNoteId ? { notebookNoteId: s.notebookNoteId } : {}),
          });
        }
        cursor += maxRowSpan;
      }
    }
    const topbar = allSlots.filter((s) => s.placement === "topbar");
    const grid = allSlots.filter((s) => s.placement !== "topbar");
    return withDefaultLayoutFallback({ slots: [...topbar, ...compactSlots(grid)] }, "sessions");
  }, [serverLayout]);

  const {
    layout,
    addWidget,
    removeWidget,
    setWidgetSize,
    moveWidget,
    swapWidgets,
    flushSave,
    compactAndFlushSave,
    updateSlotDefaultFilterValue,
    updateSlotViewMode,
  } = useActiveLayout(initialLayout, (serverLayout as any)?.id ?? null, undefined, false);

  // Sync the date filter from the pinned topbar-date-filter default once the
  // layout loads; fall back to all_time when nothing is pinned.
  useEffect(() => {
    if (filterInitialized.current || !serverLayout) return;
    filterInitialized.current = true;
    setDateRange(getDefaultDateRange(initialLayout.slots, "all_time"));
  }, [serverLayout]);

  const topbarSlots = useMemo(
    () => layout.slots.filter((s) => s.placement === "topbar").sort((a, b) => a.colStart - b.colStart),
    [layout.slots],
  );

  // — Edit-mode state —
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [layoutLibraryOpen, setLayoutLibraryOpen] = useState(false);
  const isDuplicatingRef = useRef(false);
  const { activeLayout: libraryActiveLayout, duplicate, activate } = useLayoutsLibrary("sessions");
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [pickerContext, setPickerContext] = useState<{
    colStart: number;
    rowStart: number;
    maxColSpan: number;
    context?: "topbar" | "grid";
  } | null>(null);
  const resetLayoutFn = useAction(resetLayoutContent);
  const isMobile = useIsMobile();

  // — Analytics data for session widgets (computed over the date-filtered set) —
  const metrics = useMemo(() => calculateMetrics(filteredTrades as any), [filteredTrades]);

  const analyticsData = useMemo(() => ({
    symbolStats: calculateSymbolStats(filteredTrades as any),
    monthlyStats: calculateMonthlyStats(filteredTrades as any),
    cumulativePnl: calculateCumulativePnl(filteredTrades as any).map((p) => ({ x: new Date(p.date).getTime(), y: p.cumulativePnl })),
    pnlDistribution: calculatePnlDistribution(filteredTrades as any),
    rollingWinRate: calculateRollingWinRate(filteredTrades as any),
    heatmapData: calculateHourDayHeatmap(filteredTrades as any),
    timeSlotStats: calculateTimeSlotStats(filteredTrades as any),
    holdDurationData: calculateHoldDurationData(filteredTrades as any),
    tagStats: calculateTagStats(filteredTrades as any),
    behavioralBiases: calculateBehavioralBiases(filteredTrades as any),
    sessionStats: calculateSessionStats(filteredSessions as any[], filteredTrades as any),
    pipStats: calculatePipStats(filteredTrades as any),
    pipBuckets: calculatePipBuckets(filteredTrades as any),
    pipBySymbol: calculatePipsBySymbol(filteredTrades as any),
    calmarRatio: calculateCalmarRatio(filteredTrades as any),
    recoveryFactor: calculateRecoveryFactor(filteredTrades as any),
    sizingData: calculateSizingData(filteredTrades as any),
    longShort: calculateLongShortStats(filteredTrades as any),
    dayOfWeek: calculateDayOfWeekStats(filteredTrades as any),
    streakData: calculateStreakData(filteredTrades as any),
    consistencyScore: (() => {
      const sd = calculateStreakData(filteredTrades as any);
      return calculateConsistencyScore(filteredTrades as any, sd);
    })(),
    netDailyPnl: calculateNetDailyPnl(filteredTrades as any),
    drawdownCurve: calculateDrawdownCurve(filteredTrades as any),
  }), [filteredTrades, filteredSessions]);

  // — Build the widget context —
  const sessionsData: DashboardData = {
    trades: [],
    routines: [],
    goals: [],
    accounts: accountsList as any[],
    groups: (accountGroups || []) as any[],
    strategies: (strategies || []) as any[],
    sessions: filteredSessions as any[],
    metrics,
    statsTrades: [],
    statsTradesList: filteredTrades as any[],
    equityCurveData: { points: [], totalPnl: 0, xMin: 0, xMax: 0 },
    accountEvents: [],
    fundedEvents: [],
    payoutEvents: [],
    violationsByDate: new Map(),
    pendingRoutines: [],
    topGoals: [],
    statsPeriod: "30d" as const,
    accountFilter,
    setAccountFilter,
    strategyIds,
    setStrategyIds,
    statsStartDate: dateRange.from ?? null,
    statsEndDate: dateRange.to ?? null,
    setSelectedDay: () => {},
    handleCompleteRoutine: () => {},
    setSelectedMonth: () => {},
    onAddTrade: () => setIsAddDialogOpen(true),
    onPromoteToFunded: () => {},
    onOpenPlanModal: () => setShowPlanModal(true),
    isDark,
    themeId,
    isFreePlan,
    readOnly: false,
    hasMentor: false,
    t,
    chartMode: "networth",
    setChartMode: () => {},
    analyticsData,
    charges: [],
    tptWalletContext: undefined,
    filteredAccounts: accountsList as any[],
    calendarEvents: [],
    pfmTemplateFilter: null,
    setPfmTemplateFilter: () => {},
    accountStatusFilter: null,
    setAccountStatusFilter: () => {},
    dateRange,
    setDateRange,
    calendarMonth: startOfMonth(new Date()),
    setCalendarMonth: () => {},
    showImportModal: false,
    setShowImportModal: () => {},
    showAddPayoutModal: false,
    setShowAddPayoutModal: () => {},
    isLoadingPfm: false,
    userId: user?.id,
    hiddenAccountsCount,
    showPlanModal,
    setShowPlanModal,
  };

  const extraTopbarActions = (
    <>
      {/* Edgy sits with the page's other toolbar actions. */}
      <EdgyHeaderButton />
      <Button
        size="sm"
        className="text-xs h-8 gap-1"
        onClick={() => setIsAddDialogOpen(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        {t.sessions.addSession}
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="text-xs h-8 gap-1 max-w-[140px]"
        onClick={() => setLayoutLibraryOpen(true)}
      >
        <Layers className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{(libraryActiveLayout as any)?.name ?? t.dashboard.layouts.menuTitle}</span>
        <ChevronDown className="h-3 w-3 shrink-0" />
      </Button>
      {!isEditMode && (
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-8 min-w-[5rem]"
          disabled={isDuplicating}
          onClick={async () => {
            if (isFreePlan) {
              setShowPlanModal(true);
              return;
            }
            if ((libraryActiveLayout as any)?.isReadOnly) {
              if (isDuplicatingRef.current) return;
              isDuplicatingRef.current = true;
              setIsDuplicating(true);
              let success = false;
              try {
                const created = await duplicate((libraryActiveLayout as any).id, t.dashboard.layouts.newLayout);
                if (created) {
                  const activated = await activate((created as any).id);
                  if (activated) success = true;
                }
              } finally {
                setIsDuplicating(false);
                isDuplicatingRef.current = false;
              }
              if (!success) return;
              await refetchLayout();
            }
            setIsEditMode(true);
          }}
        >
          {isDuplicating ? (
            <><Loader2 className="h-3 w-3 animate-spin" />{t.dashboard.customize}</>
          ) : (
            t.dashboard.customize
          )}
        </Button>
      )}
    </>
  );

  return (
    <PageTransition className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="sticky top-0 z-30 -mx-3 sm:-mx-5 mb-5 flex items-center justify-between gap-4 min-h-10 border-b border-border bg-background/80 px-3 sm:px-5 py-3 backdrop-blur-md"
      >
        <h1 className="flex items-center gap-2.5 text-lg font-semibold shrink-0">
          {t.sessions.title}
        </h1>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isEditMode ? (
            <TopbarDndWrapper
              slots={topbarSlots}
              onSwap={(idA, idB) => { swapWidgets(idA, idB); flushSave(); }}
            >
              {topbarSlots.map((slot) => (
                <SortableTopbarSlot
                  key={slot.id}
                  id={slot.id}
                  slot={slot}
                  isEditMode={true}
                  dateRange={dateRange}
                  accountFilter={accountFilter}
                  strategyIds={strategyIds}
                  accounts={accountsList as any[]}
                  groups={(accountGroups || []) as any[]}
                  strategies={(strategies || []) as any[]}
                  onDateRangeChange={setDateRange}
                  onAccountFilterChange={setAccountFilter}
                  onStrategyIdsChange={setStrategyIds}
                  onAddTrade={() => setIsAddDialogOpen(true)}
                  onAddPayout={() => {}}
                  onRemove={() => { removeWidget(slot.id); trackWidgetRemoved(slot.widgetKey); flushSave(); }}
                  onPin={(value) => {
                    updateSlotDefaultFilterValue(slot.id, value);
                    flushSave();
                    toastFn({ description: value === null ? t.pages.unpinDefault : t.pages.pinAsDefaultConfirm, duration: 1500 });
                  }}
                />
              ))}
            </TopbarDndWrapper>
          ) : (
            <TopbarFilterBar
              slots={topbarSlots}
              dateRange={dateRange}
              accountFilter={accountFilter}
              strategyIds={strategyIds}
              accounts={accountsList as any[]}
              groups={(accountGroups || []) as any[]}
              strategies={(strategies || []) as any[]}
              onDateRangeChange={setDateRange}
              onAccountFilterChange={setAccountFilter}
              onStrategyIdsChange={setStrategyIds}
              onAddTrade={() => setIsAddDialogOpen(true)}
              onAddPayout={() => {}}
              onRemove={() => {}}
              onPin={undefined}
              extraActions={extraTopbarActions}
            />
          )}
          {isEditMode && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => setPickerContext({ colStart: 1, rowStart: 1, maxColSpan: 6, context: "topbar" })}
            >
              {t.pages.topbarAdd}
            </Button>
          )}
          <LayoutLibraryModal
            open={layoutLibraryOpen}
            onOpenChange={setLayoutLibraryOpen}
            scope="sessions"
            isFreePlan={isFreePlan}
            onUpgradeClick={() => setShowPlanModal(true)}
            activeLayoutId={(libraryActiveLayout as any)?.id ?? null}
            onNewLayoutActivated={async () => {
              await refetchLayout();
              setIsEditMode(true);
            }}
            onBeforeActivate={async () => {
              await flushSave();
            }}
          />
        </div>
      </motion.div>

      {showOnboardingBanner && (
        <div className="mb-6 rounded-lg border border-primary/30 bg-primary/10 p-4 flex items-center gap-3">
          <span className="text-xl">⏱️</span>
          <p className="text-sm text-foreground font-medium">{t.sessions.onboardingBanner}</p>
        </div>
      )}

      {hiddenAccountsCount > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-300">
          <span>
            {t.tradingAccounts.hiddenBanner.title.replace("{{count}}", String(hiddenAccountsCount))}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-4 shrink-0 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/20"
            onClick={() => setShowPlanModal(true)}
          >
            {t.tradingAccounts.hiddenBanner.cta}
          </Button>
        </div>
      )}

      {pendingMerges.length > 0 && (
        <div className="mb-6 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4">
          <div className="flex items-center gap-2 mb-3">
            <GitMerge className="h-4 w-4 text-yellow-400" />
            <span className="font-medium text-yellow-400">{t.sessions.pendingMergeTitle}</span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">{t.sessions.pendingMergeSubtitle}</p>
          <div className="flex flex-col gap-2">
            {(pendingMerges as any[]).map((merge: any) => (
              <div key={merge.id} className="flex items-center justify-between rounded-md bg-card p-3">
                <span className="text-sm text-foreground">
                  {t.sessions.pendingMergeBanner
                    .replace(
                      "{start}",
                      new Date(merge.proposedStart).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    )
                    .replace(
                      "{end}",
                      new Date(merge.proposedEnd).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    )
                    .replace("{count}", String(merge.tradeCount))}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="default" onClick={() => handleConfirmMerge(merge.id)}>
                    {t.sessions.pendingMergeAccept}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDismissMerge(merge.id)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isEditMode && (
        <EditModeBar
          onReset={() => setShowResetDialog(true)}
          onDone={async () => { await compactAndFlushSave(); trackLayoutSaved(); setIsEditMode(false); }}
        />
      )}

      <WidgetDataProvider value={sessionsData}>
        <DashboardLayout
          layout={layout}
          isEditMode={isEditMode}
          isMobile={isMobile}
          onAddWidgetClick={(colStart, rowStart, maxColSpan) =>
            setPickerContext({ colStart, rowStart, maxColSpan })
          }
          onRemoveWidget={(slotId) => {
            const slot = layout.slots.find((s) => s.id === slotId);
            if (slot) trackWidgetRemoved(slot.widgetKey);
            removeWidget(slotId);
          }}
          onWidgetSizeChange={setWidgetSize}
          onMoveWidget={moveWidget}
          onSwapWidgets={swapWidgets}
          onUpdateSlotConfig={updateSlotDefaultFilterValue}
          onUpdateSlotViewMode={updateSlotViewMode}
        />
      </WidgetDataProvider>

      <AddWidgetPicker
        open={pickerContext !== null}
        onOpenChange={(open) => { if (!open) setPickerContext(null); }}
        layout={layout}
        maxColSpan={pickerContext?.maxColSpan ?? 1}
        rowStart={pickerContext?.rowStart ?? 1}
        colStart={pickerContext?.colStart ?? 1}
        scope="sessions"
        initialCategory={pickerContext?.context === "topbar" ? "topbar" : undefined}
        isFreePlan={isFreePlan}
        onUpgradeClick={() => setShowPlanModal(true)}
        onPick={(widgetKey) => {
          if (!pickerContext) return;
          if (TOPBAR_WIDGET_KEYS.has(widgetKey)) {
            addWidget(widgetKey, 1, 0, 1, 1, "topbar");
          } else {
            const maxOccupiedRow = layout.slots
              .filter((s) => s.placement !== "topbar")
              .reduce((m, s) => Math.max(m, s.rowStart + s.rowSpan - 1), 0);
            addWidget(widgetKey, pickerContext.colStart, maxOccupiedRow + 1, pickerContext.maxColSpan);
          }
          trackWidgetAdded(widgetKey);
          setPickerContext(null);
        }}
      />

      <ResetLayoutDialog
        open={showResetDialog}
        onOpenChange={setShowResetDialog}
        onConfirm={async () => {
          const layoutId = (serverLayout as any)?.id as string | undefined;
          if (layoutId) {
            await resetLayoutFn({ layoutId });
            await refetchLayout();
          }
          setIsEditMode(false);
        }}
      />

      {/* Add Session Dialog (+ AddTradeModal imbriquée) */}
      <AddSessionDialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} onCreated={refetchSessions} />

      <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />
    </PageTransition>
  );
}
